import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react'
import { FolderItem as FolderItemType } from '../../types'
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined'
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined'
import DriveFileRenameOutlineOutlinedIcon from '@mui/icons-material/DriveFileRenameOutlineOutlined'
import DriveFileMoveOutlinedIcon from '@mui/icons-material/DriveFileMoveOutlined'
import MoreHorizOutlinedIcon from '@mui/icons-material/MoreHorizOutlined'
import { VirtualItem } from '@tanstack/react-virtual'

type Props = {
  folder: FolderItemType
  virtualItem: VirtualItem
  selectMultipleFiles: boolean
  addFileIdToSelectedFileRef: (fileId: string) => void
  fileHasChecked: (fileId: string) => boolean
  showRenamePopup: (fileId: string) => void
  deleteFolder?: (folderItem: FolderItemType) => void
  movable: boolean
  move?: ({
    id,
    name,
    type,
  }: {
    id: string
    name: string
    type: 'folder' | 'file'
  }) => void
  onFolderClick?: (
    folder: FolderItemType,
    setFolderStack: ((stack: FolderItemType[]) => void) | undefined
  ) => void
  setFolderStack?: (stack: FolderItemType[]) => void
}

const MobileFolderItem = ({
  folder,
  virtualItem,
  selectMultipleFiles,
  addFileIdToSelectedFileRef,
  fileHasChecked,
  showRenamePopup,
  deleteFolder,
  movable,
  move,
  onFolderClick,
  setFolderStack,
}: Props) => {
  return (
    <div
      key={folder.id}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: `${virtualItem.size}px`,
        transform: `translateY(${virtualItem.start}px)`,
      }}
    >
      <li className="rounded-xl mx-3 mb-3 bg-white border border-gray-200 hover:border-green-300 hover:bg-green-50 transition-all duration-200 ease-in-out overflow-hidden">
        <div className="p-4">
          <div className="flex justify-between items-center min-h-12">
            <div
              className="flex w-full h-full items-center cursor-pointer group"
              onClick={() =>
                onFolderClick ? onFolderClick(folder, setFolderStack) : null
              }
            >
              {selectMultipleFiles && (
                <div className="flex items-center pr-3">
                  <input
                    type="checkbox"
                    name="select-all"
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 transition-colors"
                    onChange={() => addFileIdToSelectedFileRef(folder.id)}
                    checked={fileHasChecked(folder.id)}
                  />
                </div>
              )}

              <div className="flex items-center justify-center w-10 h-10 bg-green-50 rounded-lg mr-4 flex-shrink-0 border border-green-100">
                <FolderOutlinedIcon sx={{ fontSize: 20, color: '#15803D' }} />
              </div>

              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-gray-900 leading-5 truncate group-hover:text-green-700 transition-colors duration-150">
                  {folder.name}
                </h3>
                <p className="text-xs text-gray-500 mt-1 font-medium">Folder</p>
              </div>
            </div>
            {!selectMultipleFiles && (movable || !!deleteFolder) && (
              <div className="flex justify-center items-center ml-2">
                <Menu>
                  <MenuButton className="p-2.5 rounded-full bg-gray-50 text-gray-600 hover:bg-gray-100 hover:text-gray-800 transition-all duration-150 border border-gray-100">
                    <MoreHorizOutlinedIcon sx={{ fontSize: 18 }} />
                  </MenuButton>
                  <MenuItems
                    anchor="bottom end"
                    className="z-50 mt-2 bg-white rounded-xl border border-gray-200 min-w-44 py-2"
                  >
                    {movable && move && (
                      <MenuItem>
                        <button
                          className="flex w-full gap-3 items-center px-4 py-3 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-colors duration-150 first:rounded-t-xl"
                          onClick={() =>
                            move({
                              id: folder.id,
                              name: folder.name,
                              type: 'folder',
                            })
                          }
                        >
                          <DriveFileMoveOutlinedIcon sx={{ fontSize: 18 }} />
                          <span className="font-medium">Move</span>
                        </button>
                      </MenuItem>
                    )}

                    {deleteFolder && (
                      <MenuItem>
                        <button
                          className="flex w-full gap-3 items-center px-4 py-3 text-sm text-gray-700 hover:bg-red-50 hover:text-red-700 transition-colors duration-150"
                          onClick={() => deleteFolder(folder)}
                        >
                          <DeleteOutlineOutlinedIcon sx={{ fontSize: 18 }} />
                          <span className="font-medium">Delete</span>
                        </button>
                      </MenuItem>
                    )}

                    <MenuItem>
                      <button
                        className="flex w-full gap-3 items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-150 last:rounded-b-xl"
                        onClick={() => showRenamePopup(folder.id)}
                      >
                        <DriveFileRenameOutlineOutlinedIcon
                          sx={{ fontSize: 18 }}
                        />
                        <span className="font-medium">Rename</span>
                      </button>
                    </MenuItem>
                  </MenuItems>
                </Menu>
              </div>
            )}
          </div>
        </div>
      </li>
    </div>
  )
}

export default MobileFolderItem
